import { useState, useMemo } from 'react';
import {
  User,
  CreditCard,
  Building,
  Building2,
  FileText,
  Coins,
  Users,
  Calendar,
  Search,
  ChevronDown,
  ChevronRight,
  Info,
  Sparkles,
  X,
} from 'lucide-react';
import { useSystemFields, searchFieldsLocally, SystemFieldCategory, SystemField } from '../../hooks/useSystemFields';

interface SystemFieldsPanelProps {
  onInsertField: (fieldKey: string) => void;
  className?: string;
}

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  usuario: User,
  conta: CreditCard,
  tenant: Building,
  contrato: FileText,
  asset: Coins,
  signatario: Users,
  data: Calendar,
  entidade_pj: Building2,
};

const CATEGORY_COLORS: Record<string, string> = {
  usuario: 'blue',
  conta: 'green',
  tenant: 'purple',
  contrato: 'orange',
  asset: 'yellow',
  signatario: 'pink',
  data: 'cyan',
  entidade_pj: 'indigo',
};

export function SystemFieldsPanel({ onInsertField, className = '' }: SystemFieldsPanelProps) {
  const { data: categories, isLoading, error } = useSystemFields();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['usuario', 'tenant']));
  const [hoveredField, setHoveredField] = useState<SystemField | null>(null);

  const searchResults = useMemo(() => {
    if (!categories || searchQuery.length < 2) return null;
    return searchFieldsLocally(categories, searchQuery);
  }, [categories, searchQuery]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleInsertField = (field: SystemField) => {
    onInsertField(field.key);
  };

  const getCategoryIcon = (categoryId: string) => {
    return CATEGORY_ICONS[categoryId] || FileText;
  };

  const getCategoryColor = (categoryId: string) => {
    return CATEGORY_COLORS[categoryId] || 'gray';
  };

  if (isLoading) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="animate-pulse space-y-3">
          <div className="h-10 bg-surface-700 rounded-lg" />
          <div className="h-8 bg-surface-700 rounded" />
          <div className="h-8 bg-surface-700 rounded" />
          <div className="h-8 bg-surface-700 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 text-red-400 ${className}`}>
        Erro ao carregar campos de sistema
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-surface-700">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-primary-400" />
          <h3 className="font-semibold text-surface-100">Campos de Sistema</h3>
        </div>
        <p className="text-xs text-surface-500 mb-3">
          Campos auto-preenchidos com dados do banco
        </p>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar campos..."
            className="w-full pl-9 pr-8 py-2 bg-surface-800 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder-surface-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-surface-700 rounded"
            >
              <X className="w-4 h-4 text-surface-500" />
            </button>
          )}
        </div>
      </div>


      {/* Content */}
      <div className="flex-1 overflow-y-auto p-2">
        {/* Search Results */}
        {searchResults && searchResults.length > 0 ? (
          <div className="space-y-1">
            <p className="text-xs text-surface-500 px-2 py-1">
              {searchResults.length} resultado(s) encontrado(s)
            </p>
            {searchResults.map((result) => (
              <FieldItem
                key={result.field.key}
                field={result.field}
                categoryName={result.category.name}
                color={getCategoryColor(result.category.id)}
                onInsert={() => handleInsertField(result.field)}
                onHover={setHoveredField}
              />
            ))}
          </div>
        ) : searchQuery.length >= 2 ? (
          <div className="text-center py-8 text-surface-500 text-sm">
            Nenhum campo encontrado
          </div>
        ) : (
          /* Categories */
          <div className="space-y-1">
            {categories?.map((category) => {
              const Icon = getCategoryIcon(category.id);
              const color = getCategoryColor(category.id);
              const isExpanded = expandedCategories.has(category.id);

              return (
                <div key={category.id} className="rounded-lg overflow-hidden">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-surface-800 transition-colors text-left`}
                  >
                    <div className={`p-1.5 rounded bg-${color}-500/20`}>
                      <Icon className={`w-4 h-4 text-${color}-400`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-surface-200 truncate block">
                        {category.name}
                      </span>
                      <span className="text-xs text-surface-500">
                        {category.fields.length} campos
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-surface-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-surface-500" />
                    )}
                  </button>

                  {/* Category Fields */}
                  {isExpanded && (
                    <div className="pl-4 pr-2 pb-2 space-y-0.5">
                      {category.fields.map((field) => (
                        <FieldItem
                          key={field.key}
                          field={field}
                          color={color}
                          onInsert={() => handleInsertField(field)}
                          onHover={setHoveredField}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tooltip */}
      {hoveredField && (
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-surface-800 border-t border-surface-700">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-primary-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-surface-200 truncate">
                {`{{${hoveredField.key}}}`}
              </p>
              <p className="text-xs text-surface-400 mt-0.5">
                {hoveredField.description}
              </p>
              <p className="text-xs text-surface-500 mt-1">
                Ex: <span className="text-surface-300">{hoveredField.example}</span>
              </p>
              {hoveredField.requiresContext && (
                <p className="text-xs text-yellow-500 mt-1">
                  ⚠️ Requer: {hoveredField.requiresContext}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface FieldItemProps {
  field: SystemField;
  color: string;
  categoryName?: string;
  onInsert: () => void;
  onHover: (field: SystemField | null) => void;
}

function FieldItem({ field, color, categoryName, onInsert, onHover }: FieldItemProps) {
  return (
    <button
      onClick={onInsert}
      onMouseEnter={() => onHover(field)}
      onMouseLeave={() => onHover(null)}
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-${color}-500/10 transition-colors text-left group`}
    >
      <span className={`w-1.5 h-1.5 rounded-full bg-${color}-400 flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <span className="text-xs text-surface-300 truncate block">
          {field.name.replace(/_/g, ' ')}
        </span>
        {categoryName && (
          <span className="text-[10px] text-surface-500">{categoryName}</span>
        )}
      </div>
      <span className="text-[10px] text-surface-600 group-hover:text-surface-400 transition-colors">
        inserir
      </span>
    </button>
  );
}

export default SystemFieldsPanel;
